/**
 * End-of-month inventory reconciliation — overview mapping and journal entry calculation.
 * Mirrors the hub EOM worksheet (Overview, Target values, QBO values, JE tabs).
 */

import { usdNumberFromUnknown } from "../money/usd-cents.js";

export type EomPositiveSide = "Debit" | "Credit";

export interface EomOverviewRow {
  qbo_account: string;
  mv_equivalent: string;
  positive_is: EomPositiveSide;
}

export interface EomTargetValues {
  adjustment_and_wastage: number;
  prototypes: number;
  wip: number;
  inventory_asset: number;
  external_costs: number;
  wosl: number;
}

export interface EomQboValues {
  inventory_shrinkage: number;
  other_cos: number;
  wip: number;
  inventory_asset: number;
  eom_inventory_close: number;
  wo_labor_liability: number;
  wo_overhead_liabilities: number;
  wo_services_liability: number;
}

export interface EomJournalEntryLine {
  qbo_account: string;
  debit: number;
  credit: number;
}

export interface EomJournalEntry {
  lines: EomJournalEntryLine[];
  total_debit: number;
  total_credit: number;
}

export const EOM_OVERVIEW_ROWS: readonly EomOverviewRow[] = [
  { qbo_account: "Cost of Goods Sold:Inventory Shrinkage", mv_equivalent: "Adjustment and Wastage", positive_is: "Debit" },
  { qbo_account: "Prototypes for R&D", mv_equivalent: "Prototypes", positive_is: "Debit" },
  { qbo_account: "Other Costs Of Service (COS)", mv_equivalent: "0", positive_is: "Debit" },
  { qbo_account: "Work In Process Inventory - MV", mv_equivalent: "WIP", positive_is: "Debit" },
  { qbo_account: "Inventory Asset", mv_equivalent: "Inventory Asset", positive_is: "Debit" },
  { qbo_account: "Inventory Asset:EoM Inventory Close", mv_equivalent: "0", positive_is: "Debit" },
  { qbo_account: "Work Order Labor Liability", mv_equivalent: "External costs", positive_is: "Credit" },
  { qbo_account: "Work Order Overhead Liabilities", mv_equivalent: "Internal costs", positive_is: "Credit" },
  { qbo_account: "Work Order Services Liability", mv_equivalent: "WOSL", positive_is: "Credit" },
  { qbo_account: "Wages:Labor", mv_equivalent: "Offset internal costs", positive_is: "Debit" },
  { qbo_account: "Cost of Goods Sold", mv_equivalent: "Offset remainder", positive_is: "Debit" },
] as const;

export const EOM_TARGET_FIELDS: readonly (keyof EomTargetValues)[] = [
  "adjustment_and_wastage",
  "prototypes",
  "wip",
  "inventory_asset",
  "external_costs",
  "wosl",
] as const;

export const EOM_QBO_FIELDS: readonly (keyof EomQboValues)[] = [
  "inventory_shrinkage",
  "other_cos",
  "wip",
  "inventory_asset",
  "eom_inventory_close",
  "wo_labor_liability",
  "wo_overhead_liabilities",
  "wo_services_liability",
] as const;

export const EOM_TARGET_LABELS: Record<keyof EomTargetValues, string> = {
  adjustment_and_wastage: "Adjustment and Wastage",
  prototypes: "Prototypes",
  wip: "WIP",
  inventory_asset: "Inventory Asset",
  external_costs: "External costs",
  wosl: "WOSL",
};

export const EOM_QBO_LABELS: Record<keyof EomQboValues, string> = {
  inventory_shrinkage: "Cost of Goods Sold:Inventory Shrinkage",
  other_cos: "Other Costs Of Service (COS)",
  wip: "Work In Process Inventory - MV",
  inventory_asset: "Inventory Asset",
  eom_inventory_close: "Inventory Asset:EoM Inventory Close",
  wo_labor_liability: "Work Order Labor Liability",
  wo_overhead_liabilities: "Work Order Overhead Liabilities",
  wo_services_liability: "Work Order Services Liability",
};

const MV_TO_TARGET: Partial<Record<string, keyof EomTargetValues>> = {
  "Adjustment and Wastage": "adjustment_and_wastage",
  Prototypes: "prototypes",
  WIP: "wip",
  "Inventory Asset": "inventory_asset",
  "External costs": "external_costs",
  WOSL: "wosl",
};

const QBO_ACCOUNT_TO_FIELD: Partial<Record<string, keyof EomQboValues>> = {
  "Cost of Goods Sold:Inventory Shrinkage": "inventory_shrinkage",
  "Other Costs Of Service (COS)": "other_cos",
  "Work In Process Inventory - MV": "wip",
  "Inventory Asset": "inventory_asset",
  "Inventory Asset:EoM Inventory Close": "eom_inventory_close",
  "Work Order Labor Liability": "wo_labor_liability",
  "Work Order Overhead Liabilities": "wo_overhead_liabilities",
  "Work Order Services Liability": "wo_services_liability",
};

export function parseEomReconciliationMonth(value: string): string {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error("Month must be a YYYY-MM-DD date (first of month).");
  }
  const parsed = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Month must be a valid calendar date.");
  }
  if (parsed.getUTCDate() !== 1) {
    throw new Error("Month must be the first day of the month (YYYY-MM-01).");
  }
  return trimmed;
}

export function firstOfUtcMonthFromDate(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function targetValueForMv(mvEquivalent: string, target: EomTargetValues): number {
  if (mvEquivalent === "0" || mvEquivalent.trim() === "") {
    return 0;
  }
  const key = MV_TO_TARGET[mvEquivalent];
  return key ? target[key] : 0;
}

function qboValueForAccount(qboAccount: string, qbo: EomQboValues): number {
  const key = QBO_ACCOUNT_TO_FIELD[qboAccount];
  return key ? qbo[key] : 0;
}

function sideAmounts(diff: number, positiveIs: EomPositiveSide): { debit: number; credit: number } {
  if (positiveIs === "Debit") {
    return {
      debit: diff > 0 ? diff : 0,
      credit: diff < 0 ? -diff : 0,
    };
  }
  return {
    debit: diff < 0 ? -diff : 0,
    credit: diff > 0 ? diff : 0,
  };
}

export const EOM_COGS_OFFSET_ACCOUNT = "Cost of Goods Sold";

export function summarizeEomJournalEntry(journalEntry: EomJournalEntry): {
  magnitude_of_je: number;
  target_cogs: number;
} {
  const cogsLine = journalEntry.lines.find((line) => line.qbo_account === EOM_COGS_OFFSET_ACCOUNT);
  return {
    magnitude_of_je: journalEntry.total_debit,
    target_cogs: cogsLine ? Math.max(cogsLine.debit, cogsLine.credit) : 0,
  };
}

export function computeEomJournalEntry(
  target: EomTargetValues,
  qbo: EomQboValues,
  overviewRows: readonly EomOverviewRow[] = EOM_OVERVIEW_ROWS,
): EomJournalEntry {
  const working = overviewRows.map((row) => {
    const diff = targetValueForMv(row.mv_equivalent, target) - qboValueForAccount(row.qbo_account, qbo);
    const { debit, credit } = sideAmounts(diff, row.positive_is);
    const isWages = row.mv_equivalent === "Offset internal costs";
    const isCogs = row.mv_equivalent === "Offset remainder";
    return {
      qbo_account: row.qbo_account,
      mv_equivalent: row.mv_equivalent,
      debBase: isWages || isCogs ? 0 : debit,
      credBase: isWages || isCogs ? 0 : credit,
      isWages,
      isCogs,
    };
  });

  const internalDeb = working
    .filter((row) => row.mv_equivalent === "Internal costs")
    .reduce((sum, row) => sum + row.debBase, 0);
  const internalCred = working
    .filter((row) => row.mv_equivalent === "Internal costs")
    .reduce((sum, row) => sum + row.credBase, 0);

  const staged = working.map((row) => ({
    qbo_account: row.qbo_account,
    deb1: row.debBase + (row.isWages ? internalCred : 0),
    cred1: row.credBase + (row.isWages ? internalDeb : 0),
    isCogs: row.isCogs,
  }));

  const imbalance = staged.reduce((sum, row) => sum + row.deb1 - row.cred1, 0);
  const cogsDeb = imbalance < 0 ? -imbalance : 0;
  const cogsCred = imbalance > 0 ? imbalance : 0;

  const lines = staged.map((row) => ({
    qbo_account: row.qbo_account,
    debit: roundMoney(row.deb1 + (row.isCogs ? cogsDeb : 0)),
    credit: roundMoney(row.cred1 + (row.isCogs ? cogsCred : 0)),
  }));

  const totalDebit = roundMoney(lines.reduce((sum, line) => sum + line.debit, 0));
  const totalCredit = roundMoney(lines.reduce((sum, line) => sum + line.credit, 0));

  return {
    lines,
    total_debit: totalDebit,
    total_credit: totalCredit,
  };
}

export function emptyEomTargetValues(): EomTargetValues {
  return {
    adjustment_and_wastage: 0,
    prototypes: 0,
    wip: 0,
    inventory_asset: 0,
    external_costs: 0,
    wosl: 0,
  };
}

export function emptyEomQboValues(): EomQboValues {
  return {
    inventory_shrinkage: 0,
    other_cos: 0,
    wip: 0,
    inventory_asset: 0,
    eom_inventory_close: 0,
    wo_labor_liability: 0,
    wo_overhead_liabilities: 0,
    wo_services_liability: 0,
  };
}

export function parseEomNumericField(value: unknown, fieldLabel: string): number {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numeric = usdNumberFromUnknown(value);
  if (numeric == null) {
    throw new Error(`${fieldLabel} must be a number.`);
  }
  return numeric;
}

export function parseEomTargetValues(body: unknown): EomTargetValues {
  if (body === null || typeof body !== "object") {
    throw new Error("Target values body must be an object.");
  }
  const record = body as Record<string, unknown>;
  const parsed = {} as EomTargetValues;
  for (const key of EOM_TARGET_FIELDS) {
    parsed[key] = parseEomNumericField(record[key], EOM_TARGET_LABELS[key]);
  }
  return parsed;
}

export function parseEomQboValues(body: unknown): EomQboValues {
  if (body === null || typeof body !== "object") {
    throw new Error("QBO values body must be an object.");
  }
  const record = body as Record<string, unknown>;
  const parsed = {} as EomQboValues;
  for (const key of EOM_QBO_FIELDS) {
    parsed[key] = parseEomNumericField(record[key], EOM_QBO_LABELS[key]);
  }
  return parsed;
}

export function parseEomQboJeSubmitBody(body: unknown): { qbo_je_name: string; qbo_je_url: string } {
  if (body === null || typeof body !== "object") {
    throw new Error("Submit body must be an object.");
  }
  const record = body as Record<string, unknown>;
  const qboJeName = typeof record.qbo_je_name === "string" ? record.qbo_je_name.trim() : "";
  const qboJeUrl = typeof record.qbo_je_url === "string" ? record.qbo_je_url.trim() : "";
  if (qboJeName === "") {
    throw new Error("Journal entry name is required.");
  }
  if (qboJeUrl === "") {
    throw new Error("QuickBooks Online journal entry URL is required.");
  }
  if (!/^https?:\/\//i.test(qboJeUrl)) {
    throw new Error("QuickBooks Online journal entry URL must start with http:// or https://.");
  }
  return { qbo_je_name: qboJeName, qbo_je_url: qboJeUrl };
}
